import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../App';
import { useAuth } from '../AuthContext';
import { t } from '../i18n';
import LanguageToggle from '../components/game/LanguageToggle';
import Sidebar from '../components/game/Sidebar';
import { Coins, ShoppingBag, Check, Lock, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COLORS = {
  common: { bg: '#e5e7eb', text: '#4b5563', label: 'Common' },
  rare: { bg: '#dbeafe', text: '#2563eb', label: 'Rare' },
  epic: { bg: '#ede9fe', text: '#7c3aed', label: 'Epic' },
  legendary: { bg: '#fef3c7', text: '#d97706', label: 'Legendary' },
};

export default function ShopPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [gData, setGData] = useState(null);
  const [shopItems, setShopItems] = useState([]);
  const [buying, setBuying] = useState(null);
  const [filter, setFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const isEN = language === 'en';

  const fetchData = async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const [gRes, sRes] = await Promise.all([
      fetch(`${BACKEND_URL}/api/gamification/me`, { headers }),
      fetch(`${BACKEND_URL}/api/gamification/shop`, { headers }),
    ]);
    if (gRes.ok) setGData(await gRes.json());
    if (sRes.ok) {
      const data = await sRes.json();
      setShopItems(data.items || []);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const handleBuy = async (itemId) => {
    setBuying(itemId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/gamification/shop/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_id: itemId }),
      });
      if (res.ok) {
        setToast({ type: 'success', msg: isEN ? 'Item purchased!' : 'Item dibeli!' });
        await fetchData();
      } else {
        const err = await res.json();
        setToast({ type: 'error', msg: err.detail || 'Failed' });
      }
    } catch { setToast({ type: 'error', msg: 'Network error' }); }
    setBuying(null);
    setTimeout(() => setToast(null), 3000);
  };

  const handleEquip = async (itemId) => {
    const item = shopItems.find(i => i.id === itemId);
    const endpoint = item?.type === 'pet_accessory' ? '/api/pet/equip-accessory' : '/api/gamification/equip';
    try {
      await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_id: itemId }),
      });
      setToast({ type: 'success', msg: isEN ? 'Equipped!' : 'Dipakai!' });
      await fetchData();
    } catch { }
    setTimeout(() => setToast(null), 3000);
  };

  const coins = gData?.coins || 0;
  const inventory = gData?.inventory || [];
  const equipped = gData?.equipped || {};
  const filteredItems = filter === 'all' ? shopItems : shopItems.filter(i => i.type === filter);

  const typeLabels = {
    all: isEN ? 'All' : 'Semua',
    avatar_frame: isEN ? 'Frames' : 'Bingkai',
    rope_skin: isEN ? 'Rope Skins' : 'Kulit Tali',
    title: isEN ? 'Titles' : 'Gelaran',
    pet_accessory: isEN ? 'Pet Items' : 'Aksesori Pet',
  };

  if (!user) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-content" style={{ textAlign: 'center', paddingTop: '60px' }}>
          <ShoppingBag size={48} color="#9ca3af" />
          <h2 style={{ marginTop: '16px' }}>{isEN ? 'Login to access the shop' : 'Log masuk untuk akses kedai'}</h2>
          <button className="setup-back-btn" onClick={() => navigate('/login')} style={{ marginTop: '16px' }}>{isEN ? 'Login' : 'Log Masuk'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} gData={gData} />

      <header className="dashboard-header">
        <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <h1 className="dashboard-header-title">{isEN ? 'Shop' : 'Kedai'}</h1>
        <div className="header-coins" data-testid="shop-coins">
          <Coins size={16} /> <span>{coins}</span>
        </div>
        <LanguageToggle className="dashboard-lang-toggle" />
      </header>

      <div className="dashboard-content">
        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div className={`shop-toast shop-toast-${toast.type}`}
              initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {toast.msg}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter Tabs */}
        <div className="shop-filters" data-testid="shop-filters">
          {Object.entries(typeLabels).map(([key, label]) => (
            <button key={key} className={`shop-filter-btn ${filter === key ? 'shop-filter-active' : ''}`}
              onClick={() => setFilter(key)}>{label}</button>
          ))}
        </div>

        {/* Items Grid */}
        <div className="shop-grid" data-testid="shop-grid">
          {filteredItems.map((item) => {
            const rarity = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
            const owned = item.owned || inventory.includes(item.id);
            const isEquipped = Object.values(equipped).includes(item.id);
            const canAfford = coins >= item.cost;

            return (
              <motion.div key={item.id} className="shop-item" data-testid={`shop-item-${item.id}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -2 }}>
                <div className="shop-item-icon" style={{ background: rarity.bg }}>
                  <span style={{ fontSize: '28px' }}>{item.icon}</span>
                </div>
                <div className="shop-item-info">
                  <span className="shop-item-name">{isEN ? item.name_en : item.name_bm}</span>
                  <span className="shop-item-rarity" style={{ color: rarity.text }}>{rarity.label}</span>
                </div>
                {owned ? (
                  isEquipped ? (
                    <div className="shop-item-equipped"><Check size={14} /> {isEN ? 'Equipped' : 'Dipakai'}</div>
                  ) : (
                    <button className="shop-item-equip-btn" onClick={() => handleEquip(item.id)}>
                      {isEN ? 'Equip' : 'Pakai'}
                    </button>
                  )
                ) : (
                  <button className={`shop-item-buy-btn ${!canAfford ? 'shop-item-locked' : ''}`}
                    onClick={() => canAfford && handleBuy(item.id)}
                    disabled={!canAfford || buying === item.id}>
                    {buying === item.id ? '...' : (
                      <><Coins size={14} /> {item.cost}</>
                    )}
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>

        <button className="setup-back-btn" onClick={() => navigate('/')} style={{ marginTop: '24px' }}>
          &larr; {t('backToHome', language)}
        </button>
      </div>
    </div>
  );
}
